import { invariantResponse } from '@epic-web/invariant'
import { useEffect, useState } from 'react'
import { Link, useSearchParams } from 'react-router'
import { WantToGoButton } from '#app/components/want-to-go-button.tsx'
import {
	getSpecialFilter,
	generateCuisineFilter,
	FEATURED_SPECIAL_FILTERS,
	type NearMeFilterConfig,
} from '#app/data/near-me-filters.ts'
import {
	getScoreContext,
	getScoreExplainer,
	getScoreSupportText,
} from '#app/utils/consensus.shared.ts'
import {
	checkCuisineExists,
	getTopCuisinesForChips,
	slugToCuisine,
} from '#app/utils/cuisine-utils.server.ts'
import { prisma } from '#app/utils/db.server.ts'
import { getDomainUrl } from '#app/utils/misc.tsx'
import { useGeolocation } from '#app/utils/use-geolocation.ts'
import { type Route } from './+types/london.near-me.$filter.tsx'

// Haversine formula for client-side distance calculation
function haversineDistance(
	lat1: number,
	lng1: number,
	lat2: number,
	lng2: number,
): number {
	const R = 6371 // Earth's radius in km
	const dLat = ((lat2 - lat1) * Math.PI) / 180
	const dLng = ((lng2 - lng1) * Math.PI) / 180
	const a =
		Math.sin(dLat / 2) * Math.sin(dLat / 2) +
		Math.cos((lat1 * Math.PI) / 180) *
			Math.cos((lat2 * Math.PI) / 180) *
			Math.sin(dLng / 2) *
			Math.sin(dLng / 2)
	const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
	return R * c
}

export const meta: Route.MetaFunction = ({ data }) => {
	if (!data?.filterConfig) {
		return [
			{ title: 'Not Found | Good to Order' },
			{ name: 'robots', content: 'noindex' },
		]
	}

	const { filterConfig, restaurantCount, canonical } = data
	const year = new Date().getFullYear()
	const title = `${filterConfig.seoTitle} (${year}) | What to Order First`
	const description = buildNearMeMetaDescription(
		filterConfig.title,
		restaurantCount,
	)

	return [
		{ title },
		{ name: 'description', content: description },
		{ property: 'og:title', content: title },
		{ property: 'og:description', content: description },
		{ property: 'og:type', content: 'website' },
		{ property: 'og:url', content: canonical },
		{ name: 'twitter:card', content: 'summary_large_image' },
		{ rel: 'canonical', href: canonical },
	]
}

function buildNearMeMetaDescription(
	title: string,
	restaurantCount: number,
): string {
	const count = restaurantCount > 0 ? `${restaurantCount} nearby picks.` : ''
	if (/^Cheap Eats\b/i.test(title)) {
		return `${count} Affordable places near you, with what to order first at each one.`.trim()
	}
	if (/^Coffee\b/i.test(title)) {
		return `${count} Coffee shops near you, with the standout order or pastry to start with.`.trim()
	}
	if (/^Brunch\b/i.test(title)) {
		return `${count} Brunch spots near you, with the best dish to start with at each place.`.trim()
	}
	return `${count} Use your location to find nearby places fast, plus what to order first at each one.`.trim()
}

export async function loader({ params, request }: Route.LoaderArgs) {
	const { filter } = params
	invariantResponse(filter, 'Filter is required')

	const url = new URL(request.url)
	const canonical = `${getDomainUrl(request)}/london/near-me/${filter}`

	// Check for optional lat/lng in URL (for shareable links)
	const urlLat = url.searchParams.get('lat')
	const urlLng = url.searchParams.get('lng')

	// First, check if it's a special filter (price, quality, food-type)
	let filterConfig: NearMeFilterConfig | null = getSpecialFilter(filter)

	// If not a special filter, check if it's a valid cuisine in the database
	if (!filterConfig) {
		const cuisineCheck = await checkCuisineExists(filter)
		if (cuisineCheck) {
			filterConfig = generateCuisineFilter(cuisineCheck.cuisine, filter)
		}
	}

	// 404 if no valid filter found
	invariantResponse(filterConfig, 'Invalid filter', { status: 404 })

	// Fetch popular cuisines for filter chips
	const topCuisines = await getTopCuisinesForChips(8)

	// Build where clause from filter config
	const whereClause: Record<string, unknown> = {
		status: 'published',
		citySlug: 'london',
		...filterConfig.whereClause,
	}

	// Fetch all matching restaurants (no location filtering on server)
	const restaurants = await prisma.restaurant.findMany({
		where: whereClause,
		orderBy: { consensusScore: 'desc' },
		select: {
			id: true,
			name: true,
			slug: true,
			cuisine: true,
			priceRange: true,
			consensusScore: true,
			summaryOneLiner: true,
			visited: true,
			isHiddenGem: true,
			isCultClassic: true,
			picks: {
				where: { type: 'safe_first' },
				orderBy: { sortOrder: 'asc' },
				take: 1,
				select: {
					title: true,
					tip: true,
				},
			},
			locations: {
				take: 1,
				orderBy: { sortOrder: 'asc' },
				select: {
					latitude: true,
					longitude: true,
					address: true,
					mapUrl: true,
				},
			},
		},
		take: 100, // Limit for performance
	})

	// Format restaurants with location data
	const formattedRestaurants = restaurants.map((r) => ({
		id: r.id,
		name: r.name,
		slug: r.slug,
		cuisine: r.cuisine,
		priceRange: r.priceRange,
		consensusScore: r.consensusScore,
		summaryOneLiner: r.summaryOneLiner,
		visited: r.visited,
		isHiddenGem: r.isHiddenGem,
		isCultClassic: r.isCultClassic,
		safeFirstOrder: r.picks[0]?.title ?? null,
		safeFirstTip: r.picks[0]?.tip ?? null,
		latitude: r.locations[0]?.latitude ?? null,
		longitude: r.locations[0]?.longitude ?? null,
		address: r.locations[0]?.address ?? null,
		mapUrl: r.locations[0]?.mapUrl ?? null,
	}))

	return {
		filterConfig,
		filter,
		restaurants: formattedRestaurants,
		restaurantCount: formattedRestaurants.length,
		canonical,
		urlLat: urlLat ? parseFloat(urlLat) : null,
		urlLng: urlLng ? parseFloat(urlLng) : null,
		topCuisines,
	}
}

type Restaurant = {
	id: string
	name: string
	slug: string
	cuisine: string | null
	priceRange: string | null
	consensusScore: number | null
	summaryOneLiner: string | null
	visited: boolean
	isHiddenGem: boolean
	isCultClassic: boolean
	safeFirstOrder: string | null
	safeFirstTip: string | null
	latitude: number | null
	longitude: number | null
	address: string | null
	mapUrl: string | null
	distance_km?: number
}

type Coordinates = {
	lat: number
	lng: number
}

export default function NearMeFilterPage({ loaderData }: Route.ComponentProps) {
	const {
		canonical,
		filterConfig,
		filter,
		restaurants: serverRestaurants,
		urlLat,
		urlLng,
		topCuisines,
	} = loaderData

	const [searchParams, setSearchParams] = useSearchParams()
	const {
		coordinates: geoCoords,
		error: geoError,
		loading: geoLoading,
		requestLocation,
	} = useGeolocation()

	const [userLocation, setUserLocation] = useState<Coordinates | null>(
		urlLat && urlLng ? { lat: urlLat, lng: urlLng } : null,
	)
	const [sortedRestaurants, setSortedRestaurants] =
		useState<Restaurant[]>(serverRestaurants)
	const [postcode, setPostcode] = useState('')
	const [postcodeError, setPostcodeError] = useState<string | null>(null)
	const [postcodeLoading, setPostcodeLoading] = useState(false)

	// Handle geolocation success
	useEffect(() => {
		if (geoCoords && !userLocation) {
			setUserLocation(geoCoords)
			// Update URL with coords for sharing
			const newParams = new URLSearchParams(searchParams)
			newParams.set('lat', geoCoords.lat.toFixed(4))
			newParams.set('lng', geoCoords.lng.toFixed(4))
			setSearchParams(newParams, { replace: true })
		}
	}, [geoCoords, userLocation, searchParams, setSearchParams])

	// Sort restaurants by distance when location is available
	useEffect(() => {
		if (userLocation) {
			const withDistance = serverRestaurants
				.map((r) => ({
					...r,
					distance_km:
						r.latitude && r.longitude
							? haversineDistance(
									userLocation.lat,
									userLocation.lng,
									r.latitude,
									r.longitude,
								)
							: Infinity,
				}))
				.sort((a, b) => a.distance_km - b.distance_km)
			setSortedRestaurants(withDistance)
		} else {
			setSortedRestaurants(serverRestaurants)
		}
	}, [userLocation, serverRestaurants])

	const handlePostcodeSearch = async (e: React.FormEvent) => {
		e.preventDefault()
		const trimmedPostcode = postcode.trim().toUpperCase()

		if (!trimmedPostcode) {
			setPostcodeError('Please enter a postcode')
			return
		}

		setPostcodeLoading(true)
		setPostcodeError(null)

		try {
			const res = await fetch(
				`https://api.postcodes.io/postcodes/${encodeURIComponent(trimmedPostcode)}`,
			)
			const data = (await res.json()) as {
				status: number
				result?: { latitude: number; longitude: number }
				error?: string
			}

			if (data.status !== 200 || !data.result) {
				setPostcodeError('Invalid postcode. Please check and try again.')
				return
			}

			const coords = {
				lat: data.result.latitude,
				lng: data.result.longitude,
			}
			setUserLocation(coords)

			// Update URL with coords for sharing
			const newParams = new URLSearchParams(searchParams)
			newParams.set('lat', coords.lat.toFixed(4))
			newParams.set('lng', coords.lng.toFixed(4))
			setSearchParams(newParams, { replace: true })
		} catch {
			setPostcodeError('Failed to look up postcode. Please try again.')
		} finally {
			setPostcodeLoading(false)
		}
	}

	const handleClearLocation = () => {
		setUserLocation(null)
		const newParams = new URLSearchParams(searchParams)
		newParams.delete('lat')
		newParams.delete('lng')
		setSearchParams(newParams, { replace: true })
	}

	const canonicalOrigin = (() => {
		try {
			return new URL(canonical).origin
		} catch {
			return ''
		}
	})()
	const ldBreadcrumb = {
		'@context': 'https://schema.org',
		'@type': 'BreadcrumbList',
		itemListElement: [
			{
				'@type': 'ListItem',
				position: 1,
				name: 'Home',
				item: `${canonicalOrigin}/`,
			},
			{
				'@type': 'ListItem',
				position: 2,
				name: 'London',
				item: `${canonicalOrigin}/london`,
			},
			{
				'@type': 'ListItem',
				position: 3,
				name: 'Near Me',
				item: `${canonicalOrigin}/london/near-me`,
			},
			{
				'@type': 'ListItem',
				position: 4,
				name: filterConfig.title,
				item: canonical,
			},
		],
	}
	const ldItemList = {
		'@context': 'https://schema.org',
		'@type': 'ItemList',
		name: filterConfig.title,
		description: filterConfig.description,
		numberOfItems: serverRestaurants.length,
		itemListElement: serverRestaurants.map((restaurant, index) => ({
			'@type': 'ListItem',
			position: index + 1,
			item: {
				'@type': 'Restaurant',
				name: restaurant.name,
				url: `${canonicalOrigin}/london/restaurants/${restaurant.slug}`,
				servesCuisine: restaurant.cuisine ?? undefined,
				priceRange: restaurant.priceRange ?? undefined,
				address: restaurant.address
					? {
							'@type': 'PostalAddress',
							streetAddress: restaurant.address,
							addressRegion: 'London',
							addressCountry: 'GB',
						}
					: undefined,
				geo:
					restaurant.latitude != null && restaurant.longitude != null
						? {
								'@type': 'GeoCoordinates',
								latitude: restaurant.latitude,
								longitude: restaurant.longitude,
							}
						: undefined,
			},
		})),
	}

	return (
		<main className="min-h-screen bg-[#05050a] text-[#f8f8ff]">
			<script
				type="application/ld+json"
				dangerouslySetInnerHTML={{ __html: JSON.stringify(ldBreadcrumb) }}
			/>
			<script
				type="application/ld+json"
				dangerouslySetInnerHTML={{ __html: JSON.stringify(ldItemList) }}
			/>
			{/* Hero Section */}
			<section className="border-b border-[#1a1a2e] bg-gradient-to-b from-[#000000] via-[#05050a] to-[#0a001f]">
				<div className="container mx-auto px-6 py-12 lg:py-16">
					{/* Breadcrumb */}
					<nav className="mb-6 flex items-center gap-2 text-sm text-[#b0b0e0]">
						<Link to="/" className="hover:text-[#422ad5]">
							Home
						</Link>
						<span>/</span>
						<Link to="/london" className="hover:text-[#422ad5]">
							London
						</Link>
						<span>/</span>
						<Link to="/london/near-me" className="hover:text-[#422ad5]">
							Near Me
						</Link>
						<span>/</span>
						<span className="text-[#f8f8ff]">
							{filterConfig.title.replace(' Near You', '')}
						</span>
					</nav>

					<div className="max-w-3xl space-y-4">
						<h1 className="text-4xl font-black tracking-tight text-[#f8f8ff] sm:text-5xl">
							{filterConfig.title}
						</h1>
						<p className="text-lg text-[#b0b0e0]">{filterConfig.description}</p>
						<p className="max-w-2xl text-sm text-[#7070a0]">
							Every place here is handpicked. Signal score shows how strong and
							consistent the review coverage is, not a star rating.
						</p>
						<p className="text-sm text-[#7070a0]">
							{sortedRestaurants.length} restaurants found
							{userLocation && ' · Sorted by distance'}
						</p>
					</div>
				</div>
			</section>

			{/* Filter Chips */}
			<section className="border-b border-[#1a1a2e]">
				<div className="container mx-auto px-6 py-4">
					<div className="scrollbar-hide -mb-2 flex items-center gap-2 overflow-x-auto pb-2">
						<Link
							to="/london/near-me"
							className="btn btn-sm border border-[#1a1a2e] bg-[#1a1a2e] whitespace-nowrap text-[#b0b0e0] hover:bg-[#1a1a2e]/80"
						>
							All Near Me
						</Link>
						{/* Special filters (price, quality) */}
						{FEATURED_SPECIAL_FILTERS.map((filterSlug) => {
							const filterData = getSpecialFilter(filterSlug)
							if (!filterData) return null
							const isActive = filterSlug === filter
							return (
								<Link
									key={filterSlug}
									to={`/london/near-me/${filterSlug}`}
									className={`btn btn-sm whitespace-nowrap ${
										isActive
											? 'border-[#422ad5] bg-[#422ad5] text-white'
											: 'border-[#1a1a2e] bg-[#1a1a2e] text-[#b0b0e0] hover:bg-[#1a1a2e]/80'
									}`}
								>
									{filterData.title.replace(' Near You', '')}
								</Link>
							)
						})}
						{/* Dynamic cuisines from database */}
						{topCuisines.map((cuisine) => {
							const isActive = cuisine.slug === filter
							return (
								<Link
									key={cuisine.slug}
									to={`/london/near-me/${cuisine.slug}`}
									className={`btn btn-sm whitespace-nowrap ${
										isActive
											? 'border-[#422ad5] bg-[#422ad5] text-white'
											: 'border-[#1a1a2e] bg-[#1a1a2e] text-[#b0b0e0] hover:bg-[#1a1a2e]/80'
									}`}
								>
									{cuisine.cuisine}
								</Link>
							)
						})}
					</div>
				</div>
			</section>

			{/* Location Banner */}
			{!userLocation && (
				<section className="border-b border-[#1a1a2e] bg-[#0d0d15]">
					<div className="container mx-auto px-6 py-6">
						<div className="flex flex-col gap-4 md:flex-row md:items-center md:gap-6">
							<div className="flex-1">
								<h2 className="mb-1 text-lg font-semibold text-[#f8f8ff]">
									Enable location for distance-based sorting
								</h2>
								<p className="text-sm text-[#b0b0e0]">
									See which restaurants are closest to you
								</p>
							</div>
							<div className="flex flex-col gap-3 sm:flex-row">
								<button
									onClick={requestLocation}
									disabled={geoLoading}
									className="btn gap-2 border border-[#00e0ff] bg-[#00e0ff]/10 text-[#00e0ff] hover:bg-[#00e0ff]/20"
								>
									{geoLoading ? (
										<span className="loading loading-spinner loading-sm" />
									) : (
										<svg
											xmlns="http://www.w3.org/2000/svg"
											fill="none"
											viewBox="0 0 24 24"
											strokeWidth={1.5}
											stroke="currentColor"
											className="size-5"
										>
											<path
												strokeLinecap="round"
												strokeLinejoin="round"
												d="M15 10.5a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z"
											/>
											<path
												strokeLinecap="round"
												strokeLinejoin="round"
												d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1 1 15 0Z"
											/>
										</svg>
									)}
									Use My Location
								</button>
								<form onSubmit={handlePostcodeSearch} className="flex gap-2">
									<input
										type="text"
										value={postcode}
										onChange={(e) => setPostcode(e.target.value)}
										placeholder="Or enter postcode"
										className="w-32 rounded-lg border border-[#1a1a2e] bg-[#151525] px-3 py-2 text-[#f8f8ff] placeholder-[#7070a0] focus:border-[#422ad5] focus:outline-none"
									/>
									<button
										type="submit"
										disabled={postcodeLoading}
										className="btn border-none bg-[#422ad5] text-white hover:bg-[#422ad5]/80"
									>
										{postcodeLoading ? (
											<span className="loading loading-spinner loading-sm" />
										) : (
											'Go'
										)}
									</button>
								</form>
							</div>
						</div>
						{geoError && (
							<p className="mt-3 text-sm text-[#ff2e63]">
								{geoError.code === 1
									? 'Location access denied. Enter your postcode instead.'
									: 'Unable to get location. Enter your postcode instead.'}
							</p>
						)}
						{postcodeError && (
							<p className="mt-3 text-sm text-[#ff2e63]">{postcodeError}</p>
						)}
					</div>
				</section>
			)}

			{/* Location Active Banner */}
			{userLocation && (
				<section className="border-b border-[#1a1a2e] bg-[#0d0d15]/50">
					<div className="container mx-auto px-6 py-3">
						<div className="flex items-center justify-between">
							<p className="text-sm text-[#b0b0e0]">
								<span className="text-[#00e0ff]">Location enabled</span> ·
								Sorted by distance
							</p>
							<button
								onClick={handleClearLocation}
								className="btn btn-sm border-none bg-transparent text-[#7070a0] hover:text-[#f8f8ff]"
							>
								Clear
							</button>
						</div>
					</div>
				</section>
			)}

			{/* Restaurant Grid */}
			<section className="container mx-auto px-6 py-12">
				{sortedRestaurants.length === 0 ? (
					<div className="mx-auto max-w-lg py-12 text-center">
						<div className="rounded-lg border border-[#1a1a2e] bg-[#0d0d15] p-8">
							<h2 className="mb-4 text-xl font-bold text-[#f8f8ff]">
								No restaurants found
							</h2>
							<p className="mb-6 text-[#b0b0e0]">
								We couldn't find any restaurants matching this filter. Try a
								different category.
							</p>
							<Link
								to="/london/near-me"
								className="btn border-none bg-[#422ad5] text-white hover:bg-[#422ad5]/80"
							>
								View All Near Me
							</Link>
						</div>
					</div>
				) : (
					<div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
						{sortedRestaurants.map((restaurant, index) => (
							<RestaurantCard
								key={restaurant.id}
								restaurant={restaurant}
								rank={index + 1}
								hasLocation={!!userLocation}
							/>
						))}
					</div>
				)}
			</section>
		</main>
	)
}

function RestaurantCard({
	restaurant,
	rank,
	hasLocation,
}: {
	restaurant: Restaurant
	rank: number
	hasLocation: boolean
}) {
	const scoreContext = getScoreContext(restaurant.consensusScore ?? 0)
	const distanceDisplay =
		restaurant.distance_km !== undefined && restaurant.distance_km !== Infinity
			? restaurant.distance_km < 1
				? `${Math.round(restaurant.distance_km * 1000)}m away`
				: `${restaurant.distance_km.toFixed(1)}km away`
			: null

	return (
		<article className="card border border-[#1a1a2e] bg-[#0d0d15] shadow-[0_0_20px_rgba(66,42,213,0.12)] transition hover:shadow-[0_0_28px_rgba(0,224,255,0.2)]">
			<div className="card-body space-y-4 p-5">
				{/* Header with name and score */}
				<div className="flex items-start justify-between gap-3">
					<div className="flex items-center gap-3">
						<span className="badge badge-lg border border-[#422ad5] font-bold text-[#422ad5]">
							{rank}
						</span>
						<div>
							<Link
								to={`/london/restaurants/${restaurant.slug}`}
								className="card-title text-[#f8f8ff] hover:text-[#00e0ff]"
							>
								{restaurant.name}
							</Link>
							<p className="mt-1 text-xs tracking-[0.1em] text-[#b0b0e0] uppercase">
								{restaurant.cuisine} · {restaurant.priceRange}
							</p>
						</div>
					</div>
					{restaurant.consensusScore !== null && (
						<span
							className="badge badge-sm gap-1 font-semibold"
							style={{
								borderColor: scoreContext.color,
								color: scoreContext.color,
								backgroundColor: `${scoreContext.color}1a`,
							}}
							title={getScoreExplainer(restaurant.consensusScore)}
						>
							{restaurant.consensusScore}
							<span className="text-[10px] tracking-[0.18em] text-[#b0b0e0] uppercase">
								Signal
							</span>
						</span>
					)}
				</div>

				{/* Distance and badges */}
				<div className="flex flex-wrap gap-2">
					{hasLocation && distanceDisplay && (
						<span className="badge badge-sm border border-[#00e0ff] bg-[#00e0ff]/10 px-3 py-1 text-[#00e0ff]">
							{distanceDisplay}
						</span>
					)}
					{restaurant.visited && (
						<span className="badge badge-sm border border-[#00e0ff] tracking-[0.1em] text-[#00e0ff] uppercase">
							Visited
						</span>
					)}
					{restaurant.isHiddenGem && (
						<span className="badge badge-sm border border-[#00e0ff] tracking-[0.1em] text-[#00e0ff] uppercase">
							Hidden Gem
						</span>
					)}
					{restaurant.isCultClassic && (
						<span className="badge badge-sm border border-[#ff9f1c] tracking-[0.1em] text-[#ff9f1c] uppercase">
							Cult Classic
						</span>
					)}
					{(restaurant.consensusScore ?? 0) < 45 && !restaurant.isHiddenGem && (
						<span className="badge badge-sm border border-[#7070a0] tracking-[0.1em] text-[#b0b0e0] uppercase">
							Light Coverage
						</span>
					)}
				</div>

				{restaurant.safeFirstOrder && (
					<div className="rounded-lg border border-[#1a1a2e] bg-[#151525] p-3">
						<p className="mb-1 text-xs tracking-wide text-[#b0b0e0] uppercase">
							Start with
						</p>
						<p className="font-semibold text-[#f8f8ff]">
							{restaurant.safeFirstOrder}
						</p>
						{restaurant.safeFirstTip && (
							<p className="mt-1 text-sm text-[#b0b0e0]">
								{restaurant.safeFirstTip}
							</p>
						)}
					</div>
				)}

				{/* Summary */}
				{restaurant.summaryOneLiner && (
					<p className="line-clamp-2 text-sm text-[#b0b0e0]">
						{restaurant.summaryOneLiner}
					</p>
				)}

				<p className="text-xs text-[#7070a0]">
					{getScoreSupportText(restaurant.consensusScore ?? 0)}
				</p>

				{/* Address */}
				{restaurant.address && (
					<p className="text-xs text-[#7070a0]">{restaurant.address}</p>
				)}

				{/* Actions */}
				<div className="card-actions items-center justify-between pt-2">
					{restaurant.mapUrl && (
						<a
							href={restaurant.mapUrl}
							target="_blank"
							rel="noopener noreferrer"
							className="btn btn-ghost btn-sm gap-1 text-[#f8f8ff] hover:text-[#00e0ff]"
						>
							<svg
								xmlns="http://www.w3.org/2000/svg"
								fill="none"
								viewBox="0 0 24 24"
								strokeWidth={1.5}
								stroke="currentColor"
								className="size-4"
							>
								<path
									strokeLinecap="round"
									strokeLinejoin="round"
									d="M15 10.5a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z"
								/>
								<path
									strokeLinecap="round"
									strokeLinejoin="round"
									d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1 1 15 0Z"
								/>
							</svg>
							Directions
						</a>
					)}
					<div className="flex items-center gap-2">
						<WantToGoButton restaurantId={restaurant.id} size="sm" />
						<Link
							to={`/london/restaurants/${restaurant.slug}`}
							className="btn btn-sm border-none bg-[#422ad5] text-white hover:bg-[#422ad5]/80"
						>
							What to Order
						</Link>
					</div>
				</div>
			</div>
		</article>
	)
}
